import { Check, AlertCircle } from "lucide-react";

/**
 * StillTruePill — small badge showing the branch's most recent "still true"
 * confirmation. Renders in three states based on `still_true.last_status`:
 *
 *  yes → green "Still true · MMM YYYY" pill
 *  no  → red "no longer stands by this" pill
 *  null → renders nothing (branch hasn't been checked in yet — that's fine,
 *          absence of a badge just means "haven't circled back yet", not
 *          "outdated". Only a positive claim is shown.)
 *
 * Compact `size="sm"` is meant for Grove cards; default is for the detail
 * page.
 */
export function StillTruePill({ stillTrue, size = "md", className = "" }) {
  if (!stillTrue?.last_status) return null;
  const isYes = stillTrue.last_status === "yes";
  const small = size === "sm";
  const dateSrc = isYes ? stillTrue.last_confirmed_at : null;
  let dateLabel = "";
  if (dateSrc) {
    try {
      const d = new Date(dateSrc);
      dateLabel = d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    } catch { /* ignore */ }
  }

  if (isYes) {
    return (
      <span
        data-testid="still-true-pill-yes"
        className={`inline-flex items-center gap-1 rounded-full bg-[#4E5A3D]/12 text-[#4E5A3D] border border-[#4E5A3D]/25 ${
          small ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"
        } accent-label ${className}`}
        title="The person who planted this seed reconfirmed it's still holding up."
      >
        <Check className={small ? "w-3 h-3" : "w-3.5 h-3.5"} strokeWidth={2.5} />
        still true{dateLabel ? ` · ${dateLabel}` : ""}
      </span>
    );
  }

  // no → mild red pill (compact) — the detail page also shows a full banner
  return (
    <span
      data-testid="still-true-pill-no"
      className={`inline-flex items-center gap-1 rounded-full bg-[#B24A2A]/12 text-[#B24A2A] border border-[#B24A2A]/30 ${
        small ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"
      } accent-label ${className}`}
      title="The person who planted this seed marked it as no longer holding up."
    >
      <AlertCircle className={small ? "w-3 h-3" : "w-3.5 h-3.5"} strokeWidth={2.25} />
      no longer stands by this
    </span>
  );
}
